<template>
  <div
    class="modal fade"
    id="detailsModal"
    tabindex="-1"
    aria-labelledby="detailsModalLabel"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable sales-details-dialog">
      <div class="modal-content border-0 shadow-lg">
        <div class="modal-header border-0 pb-0">
          <div v-if="sale">
            <span class="badge sales-status-badge mb-2" :class="statusClass(sale.status)">
              {{ formatStatus(sale.status) }}
            </span>
            <h4 class="mb-1">{{ $t("sales_page.sale_label") }} {{ sale.reference_no }}</h4>
            <p class="text-muted mb-0">
              {{ sale.customer_name || sale.customer?.name || $t("sales_page.walk_in_customer") }} •
              {{ sale.warehouse_name || sale.warehouse?.name || $t("sales_page.not_available_short") }} •
              {{ formatDate(sale.created_at || sale.date) }}
            </p>
          </div>
          <div class="ms-auto d-flex align-items-center gap-2">
            <button
              v-if="sale"
              type="button"
              class="btn btn-primary btn-sm d-flex align-items-center gap-1"
              @click="printReceipt"
            >
              <i class="ri-printer-line"></i> {{ $t("sales_page.print") }}
            </button>
            <button
              type="button"
              class="btn-close shadow-none"
              data-bs-dismiss="modal"
              :aria-label="$t('common.close')"
            ></button>
          </div>
        </div>

        <div v-if="sale" class="modal-body pt-4">
          <div class="row g-3 mb-4">
            <div class="col-md-3 col-sm-6">
              <div class="summary-card">
                <span class="summary-label">{{ $t("sales_page.grand_total") }}</span>
                <h5>{{ formatCurrency(sale.grand_total) }}</h5>
              </div>
            </div>
            <div class="col-md-3 col-sm-6">
              <div class="summary-card">
                <span class="summary-label">{{ $t("sales_page.paid") }}</span>
                <h5 class="text-success">{{ formatCurrency(sale.paid) }}</h5>
              </div>
            </div>
            <div class="col-md-3 col-sm-6">
              <div class="summary-card">
                <span class="summary-label">{{ $t("sales_page.balance_due") }}</span>
                <h5 class="text-warning">{{ formatCurrency(balanceDue) }}</h5>
              </div>
            </div>
            <div class="col-md-3 col-sm-6">
              <div class="summary-card">
                <span class="summary-label">{{ $t("sales_page.payment_status") }}</span>
                <h5>
                  <span class="badge payment-status-badge" :class="paymentStatusClass(sale.payment_status)">
                    {{ formatStatus(sale.payment_status) }}
                  </span>
                </h5>
              </div>
            </div>
          </div>

          <div class="row g-4">
            <div class="col-lg-7">
              <div class="details-panel mb-4">
                <div class="panel-header">
                  <div>
                    <h5 class="mb-1">{{ $t("sales_page.sale_receipt") }}</h5>
                    <p class="text-muted mb-0">{{ $t("sales_page.review_sale_items") }}</p>
                  </div>
                </div>
                <SaleReceipt :sale="sale" />
              </div>
            </div>

            <div class="col-lg-5">
              <div class="details-panel mb-4">
                <div class="panel-header">
                  <div>
                    <h5 class="mb-1">{{ $t("sales_page.payment_activity") }}</h5>
                    <p class="text-muted mb-0">{{ $t("sales_page.manage_sale_payments") }}</p>
                  </div>
                  <button
                    v-if="balanceDue > 0"
                    class="btn btn-primary btn-sm"
                    @click="startCreatePayment"
                  >
                    <i class="ri-add-line me-1"></i>
                    {{ $t("sales_page.add_payment") }}
                  </button>
                </div>

                <div class="payment-list">
                  <div v-for="payment in sortedPayments" :key="payment.id" class="payment-item">
                    <div>
                      <div class="fw-semibold">{{ payment.method?.name || payment.paid_by || $t("sales_page.payment") }}</div>
                      <div class="text-muted small">
                        {{ formatDate(payment.created_at) }}
                        <span v-if="payment.note">• {{ payment.note }}</span>
                      </div>
                    </div>
                    <div class="text-end">
                      <div class="fw-semibold">{{ formatCurrency(payment.amount) }}</div>
                      <div class="payment-actions mt-2">
                        <button class="btn btn-sm btn-light" @click="startEditPayment(payment)">{{ $t("sales_page.edit") }}</button>
                        <button class="btn btn-sm btn-outline-danger" @click="removePayment(payment)">{{ $t("sales_page.delete") }}</button>
                      </div>
                    </div>
                  </div>

                  <div v-if="!sortedPayments.length" class="payment-empty-state">
                    <div class="payment-empty-icon">
                      <i class="ri-wallet-3-line"></i>
                    </div>
                    <h6 class="mb-2">{{ $t("sales_page.no_payments_yet") }}</h6>
                    <p class="text-muted mb-0">{{ $t("sales_page.no_payment_activity_yet") }}</p>
                  </div>
                </div>
              </div>

              <div v-if="showPaymentForm" class="details-panel">
                <div class="panel-header">
                  <div>
                    <h5 class="mb-1">{{ editingPaymentId ? $t("sales_page.edit_payment") : $t("sales_page.add_payment") }}</h5>
                    <p class="text-muted mb-0">
                      {{ editingPaymentId ? $t("sales_page.update_payment_entry") : $t("sales_page.record_new_payment") }}
                    </p>
                  </div>
                  <button class="btn btn-sm btn-light" @click="resetPaymentForm">{{ $t("common.cancel") }}</button>
                </div>

                <div class="row g-3">
                  <div class="col-md-6">
                    <label class="form-label">{{ $t("sales_page.amount") }}</label>
                    <input
                      v-model.number="paymentForm.amount"
                      type="number"
                      min="0"
                      step="0.01"
                      class="form-control"
                    />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">{{ $t("sales_page.method") }}</label>
                    <select v-model.number="paymentForm.method_id" class="form-select">
                      <option :value="null">{{ $t("sales_page.select_payment_method") }}</option>
                      <option v-for="method in paymentMethods" :key="method.id" :value="method.id">
                        {{ method.name }}
                      </option>
                    </select>
                  </div>
                  <div class="col-12">
                    <label class="form-label">{{ $t("sales_page.paid_by") }}</label>
                    <input
                      v-model="paymentForm.paid_by"
                      type="text"
                      class="form-control"
                      :placeholder="$t('sales_page.paid_by_placeholder')"
                    />
                  </div>
                  <div class="col-12">
                    <label class="form-label">{{ $t("sales_page.note") }}</label>
                    <textarea
                      v-model="paymentForm.note"
                      rows="3"
                      class="form-control"
                      :placeholder="$t('sales_page.optional_note')"
                    ></textarea>
                  </div>
                  <div class="col-12">
                    <button class="btn btn-primary w-100" :disabled="isSubmittingPayment" @click="submitPayment">
                      <span v-if="isSubmittingPayment" class="spinner-border spinner-border-sm me-2"></span>
                      {{ editingPaymentId ? $t("sales_page.update_payment") : $t("sales_page.save_payment") }}
                    </button>
                  </div>
                </div>
              </div>

              <div class="details-panel mt-4">
                <h5 class="mb-3">{{ $t("sales_page.sale_totals") }}</h5>
                <div class="totals-row">
                  <span>{{ $t("sales_page.subtotal") }}</span>
                  <strong>{{ formatCurrency(sale.total) }}</strong>
                </div>
                <div class="totals-row">
                  <span>{{ $t("sales_page.discount") }}</span>
                  <strong>-{{ formatCurrency(sale.discount || 0) }}</strong>
                </div>
                <div class="totals-row">
                  <span>{{ $t("sales_page.tax") }}</span>
                  <strong>{{ formatCurrency(sale.tax || 0) }}</strong>
                </div>
                <div class="totals-row">
                  <span>{{ $t("sales_page.shipping") }}</span>
                  <strong>{{ formatCurrency(sale.shipping || 0) }}</strong>
                </div>
                <div class="totals-row grand">
                  <span>{{ $t("sales_page.grand_total") }}</span>
                  <strong>{{ formatCurrency(sale.grand_total) }}</strong>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="modal-body py-5 text-center">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">{{ $t("sales_page.loading_sale_details") }}</span>
          </div>
        </div>

        <div class="modal-footer border-0 pt-0">
          <button
            type="button"
            class="btn btn-dark px-4"
            data-bs-dismiss="modal"
          >
            {{ $t("common.close") }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, reactive, ref, watch } from "vue";
import Swal from "sweetalert2";
import { useI18n } from "vue-i18n";

import type { Sale, SalePayment } from "../../../services/sales";
import salesService from "../../../services/sales";
import paymentMethodService, { type PaymentMethod } from "../../../services/paymentMethod";
import stateStore from "@/utils/store";
import SaleReceipt from "./SaleReceipt.vue";

export default defineComponent({
  name: "SalesDetails",
  components: {
    SaleReceipt,
  },
  props: {
    sale: {
      type: Object as PropType<Sale | null>,
      default: null,
    },
  },
  emits: ["payment-updated"],
  setup(props, { emit }) {
    const { t, locale } = useI18n();
    const paymentMethods = ref<PaymentMethod[]>([]);
    const showPaymentForm = ref(false);
    const editingPaymentId = ref<number | null>(null);
    const isSubmittingPayment = ref(false);

    const paymentForm = reactive({
      amount: 0,
      method_id: null as number | null,
      paid_by: "",
      note: "",
    });

    const balanceDue = computed(() => {
      const grand = Number(props.sale?.grand_total || 0);
      const paid = Number(props.sale?.paid || 0);
      return Math.max(0, grand - paid);
    });

    const sortedPayments = computed(() =>
      [...(props.sale?.payments || [])].sort((a, b) => Number(b.id || 0) - Number(a.id || 0))
    );

    const loadPaymentMethods = async () => {
      try {
        paymentMethods.value = await paymentMethodService.getPaymentMethods();
      } catch (error) {
        console.error("Error fetching payment methods:", error);
      }
    };

    const resetPaymentForm = () => {
      showPaymentForm.value = false;
      editingPaymentId.value = null;
      paymentForm.amount = 0;
      paymentForm.method_id = null;
      paymentForm.paid_by = "";
      paymentForm.note = "";
    };

    const startCreatePayment = () => {
      resetPaymentForm();
      paymentForm.amount = Number(balanceDue.value.toFixed(2));
      showPaymentForm.value = true;
    };

    const startEditPayment = (payment: SalePayment) => {
      editingPaymentId.value = payment.id;
      paymentForm.amount = Number(payment.amount || 0);
      paymentForm.method_id = payment.method_id ?? payment.method?.id ?? null;
      paymentForm.paid_by = payment.paid_by || payment.method?.name || "";
      paymentForm.note = payment.note || "";
      showPaymentForm.value = true;
    };

    const submitPayment = async () => {
      if (!props.sale?.id) return;
      if (!paymentForm.amount || paymentForm.amount <= 0) {
        await Swal.fire({ icon: "warning", title: t("sales_page.amount_required"), text: t("sales_page.enter_valid_payment_amount") });
        return;
      }
      if (!paymentForm.method_id) {
        await Swal.fire({ icon: "warning", title: t("sales_page.method_required"), text: t("sales_page.select_payment_method_text") });
        return;
      }

      isSubmittingPayment.value = true;
      try {
        const payload = {
          amount: Number(paymentForm.amount),
          method_id: paymentForm.method_id,
          paid_by: paymentForm.paid_by || "Cash",
          note: paymentForm.note,
        };

        if (editingPaymentId.value) {
          await salesService.updatePayment(props.sale.id, editingPaymentId.value, payload);
        } else {
          await salesService.addPayment(props.sale.id, payload);
        }

        await Swal.fire({
          icon: "success",
          title: editingPaymentId.value ? t("sales_page.payment_updated") : t("sales_page.payment_added"),
          timer: 1400,
          showConfirmButton: false,
        });

        resetPaymentForm();
        emit("payment-updated");
      } catch (error: any) {
        await Swal.fire({
          icon: "error",
          title: editingPaymentId.value ? t("sales_page.update_failed") : t("sales_page.create_failed"),
          text:
            error?.response?.data?.error ||
            error?.response?.data?.message ||
            t("sales_page.unable_to_save_payment"),
        });
      } finally {
        isSubmittingPayment.value = false;
      }
    };

    const removePayment = async (payment: SalePayment) => {
      if (!props.sale?.id) return;

      const result = await Swal.fire({
        title: t("sales_page.delete_payment_title"),
        text: t("sales_page.delete_payment_text"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#6c757d",
        confirmButtonText: t("sales_page.delete"),
        cancelButtonText: t("common.cancel"),
      });

      if (!result.isConfirmed) return;

      try {
        await salesService.deletePayment(props.sale.id, payment.id);
        await Swal.fire({
          icon: "success",
          title: t("sales_page.payment_deleted"),
          timer: 1400,
          showConfirmButton: false,
        });
        emit("payment-updated");
      } catch (error: any) {
        await Swal.fire({
          icon: "error",
          title: t("sales_page.delete_failed"),
          text: error?.response?.data?.message || t("sales_page.unable_to_delete_payment"),
        });
      }
    };

    const escapeHtml = (value?: string | number | null) =>
      String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");

    const printReceipt = () => {
      if (!props.sale) return;

      const sale = props.sale;
      const receiptSettings = stateStore.receiptSettings;
      const itemRows = (sale.items || [])
        .map(
          (item) => `
            <tr>
              <td class="ps-0">${escapeHtml(item.product_name)}</td>
              <td class="text-center fw-bold">${escapeHtml(item.quantity)}</td>
              <td class="text-end">${formatCurrency(item.price)}</td>
              <td class="text-end pe-0 fw-bold">${formatCurrency(item.total)}</td>
            </tr>
          `
        )
        .join("");

      const discountRow =
        Number(sale.discount || 0) > 0
          ? `
            <div class="totals-row">
              <span class="fw-bold">${t("sales_page.discount")}:</span>
              <span class="fw-bold text-danger">-${formatCurrency(sale.discount || 0)}</span>
            </div>
          `
          : "";

      const taxRow =
        Number(sale.tax || 0) > 0
          ? `
            <div class="totals-row">
              <span class="fw-bold">${t("sales_page.tax")}:</span>
              <span class="fw-bold">${formatCurrency(sale.tax || 0)}</span>
            </div>
          `
          : "";

      const shippingRow =
        receiptSettings.showTaxDiscount && Number(sale.shipping || 0) > 0
          ? `
            <div class="totals-row">
              <span class="fw-bold">${t("sales_page.shipping")}:</span>
              <span class="fw-bold">${formatCurrency(sale.shipping || 0)}</span>
            </div>
          `
          : "";

      const companyAddressRow =
        receiptSettings.showAddress && stateStore.companyAddress
          ? `<p class="mb-0">${escapeHtml(stateStore.companyAddress)}</p>`
          : "";

      const companyPhoneRow =
        receiptSettings.showPhone && stateStore.companyPhone
          ? `<p class="mb-0">${escapeHtml(t("common.phone"))}: ${escapeHtml(stateStore.companyPhone)}</p>`
          : "";

      const companyEmailRow =
        receiptSettings.showEmail && stateStore.companyEmail
          ? `<p class="mb-0">${escapeHtml(stateStore.companyEmail)}</p>`
          : "";

      const customerCell = receiptSettings.showCustomer
        ? `<div><span class="fw-bold">${escapeHtml(t("sales_page.customer"))}:</span> </div><div>${escapeHtml(
            sale.customer_name || sale.customer?.name || t("sales_page.walk_in_customer")
          )}</div>`
        : `<div></div>`;

      const barcodeRow = receiptSettings.showBarcode
        ? `<div style="text-align:center;font-size:12px;color:#64748b;margin-top:8px;">${escapeHtml(
            sale.reference_no
          )}</div>`
        : "";

      const noteRow = receiptSettings.showNoteToCustomer
        ? `<p class="mb-1 fw-bold">${escapeHtml(
            receiptSettings.noteToCustomer
          )}</p>`
          : "";

      const html = `
        <html>
          <head>
            <title>${escapeHtml(t("sales_page.sale_label"))} ${escapeHtml(sale.reference_no)}</title>
            <style>
              body {
                margin: 0;
                padding: 24px;
                background: #fff;
                color: #000;
                font-family: Arial, sans-serif;
              }
              .receipt-container {
                width: 100%;
                max-width: 500px;
                margin: 0 auto;
              }
              .text-center { text-align: center; }
              .text-end { text-align: right; }
              .fw-bold { font-weight: 700; }
              .ps-0 { padding-left: 0; }
              .pe-0 { padding-right: 0; }
              .mb-0 { margin-bottom: 0; }
              .mb-1 { margin-bottom: 6px; }
              .mb-20 { margin-bottom: 20px; }
              .mt-40 { margin-top: 40px; }
              .pt-20 { padding-top: 20px; }
              .border-top { border-top: 2px solid #000; }
              .small { font-size: 12px; }
              .text-danger { color: #d33c43; }
              .receipt-info-row,
              .totals-row {
                display: flex;
                justify-content: space-between;
                gap: 16px;
                margin-bottom: 6px;
              }
              .receipt-table {
                width: 100%;
                border-collapse: collapse;
                margin-bottom: 20px;
              }
              .receipt-table th {
                border-bottom: 2px solid #000;
                padding: 8px 0;
                font-weight: 700;
                text-transform: capitalize;
              }
              .receipt-table td {
                padding: 12px 0;
                border-bottom: 1px solid #eee;
              }
              @page {
                size: auto;
                margin: 10mm;
              }
            </style>
          </head>
          <body>
            <div class="receipt-container">
              <div class="text-center mb-20">
                <h2 style="margin: 0 0 4px;">${escapeHtml(stateStore.siteName)}</h2>
                ${companyAddressRow}
                ${companyPhoneRow}
                ${companyEmailRow}
              </div>

              <div class="mb-10">
                <div class="d-flex justify-content-between receipt-info-row">
                  <div><span class="fw-bold">${escapeHtml(t("sales_page.date"))}:</span></div>
                  <div>${escapeHtml(formatDate(sale.date || sale.created_at))}</div>
                </div>
                <div class="d-flex justify-content-between receipt-info-row">
                  ${customerCell}
                </div>
                <div class="d-flex justify-content-between receipt-info-row">
                  <div><span class="fw-bold">${escapeHtml(t("sales_page.reference_short"))}:</span></div>
                  <div>${escapeHtml(sale.reference_no)}</div>
                </div>
                <div class="d-flex justify-content-between receipt-info-row">
                  <div><span class="fw-bold">${escapeHtml(t("sales_page.warehouse"))}:</span></div>
                  <div>${escapeHtml(sale.warehouse_name || sale.warehouse?.name || t("sales_page.not_available_short"))}</div>
                </div>
                ${barcodeRow}
              </div>

              <table class="receipt-table">
                <thead>
                  <tr>
                    <th class="text-start">
                      មុខទំនិញ <br> Descriptions
                    </th>
                    <th class="text-center">
                      ចំនួន <br> Quantity
                    </th>
                    <th class="text-end">
                      តម្លៃ <br> Price
                    </th>
                    <th class="text-end pe-0">
                      តម្លៃសរុប <br> Total
                    </th>
                  </tr>
                </thead>
                <tbody>${itemRows}</tbody>
              </table>

              <div class="mb-10">
                <div class="totals-row border-top" style="padding-top: 8px;">
                  <span class="fw-bold">${escapeHtml(t("sales_page.subtotal"))}:</span>
                  <span class="fw-bold">${formatCurrency(sale.total)}</span>
                </div>
                ${receiptSettings.showTaxDiscount ? discountRow : ""}
                ${receiptSettings.showTaxDiscount ? taxRow : ""}
                ${shippingRow}
                <div class="totals-row border-top" style="padding-top: 8px;">
                  <span class="fw-bold" style="font-size: 16px;">${escapeHtml(t("sales_page.grand_total"))}:</span>
                  <span class="fw-bold" style="font-size: 16px;">${formatCurrency(sale.grand_total)}</span>
                </div>
                <div class="totals-row">
                  <span class="fw-bold">${escapeHtml(t("sales_page.paid"))}:</span>
                  <span class="fw-bold">${formatCurrency(sale.paid)}</span>
                </div>
                <div class="totals-row">
                  <span class="fw-bold">${escapeHtml(t("sales_page.due"))}:</span>
                  <span class="fw-bold">${formatCurrency(balanceDue.value)}</span>
                </div>
                <div class="totals-row">
                  <span class="fw-bold">${escapeHtml(t("sales_page.payment_method"))}:</span>
                  <span class="fw-bold">${escapeHtml(sale.paid_by || t("sales_page.cash"))}</span>
                </div>
              </div>

              <div class="text-center pt-20 border-top">
                ${noteRow}
                <!-- <p class="mb-0 small">${escapeHtml(t("sales_page.powered_by", { name: stateStore.siteName }))}</p> -->
                <p class="text-muted small">Powered by ProCare Digital Marketing</p>
              </div>
            </div>
          </body>
        </html>
      `;

      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";
      document.body.appendChild(iframe);

      const iframeWindow = iframe.contentWindow;
      const doc = iframeWindow?.document;
      if (!doc || !iframeWindow) {
        document.body.removeChild(iframe);
        return;
      }

      let hasPrinted = false;
      const triggerPrint = () => {
        if (hasPrinted) return;
        hasPrinted = true;
        iframeWindow.focus();
        iframeWindow.print();
        setTimeout(() => {
          document.body.removeChild(iframe);
        }, 1500);
      };

      iframe.onload = triggerPrint;

      doc.open();
      doc.write(html);
      doc.close();

      setTimeout(triggerPrint, 600);
    };

    const formatDate = (dateStr?: string) => {
      if (!dateStr) return t("sales_page.not_available_short");
      const date = new Date(dateStr);
      return date.toLocaleString(locale.value === "km" ? "km-KH" : "en-GB", {
        timeZone: "Asia/Phnom_Penh",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });
    };

    const formatStatus = (status?: string) => {
      const value = String(status || "pending").toLowerCase();
      const statusKeyMap: Record<string, string> = {
        pending: "sales_page.status_pending",
        sent: "sales_page.status_sent",
        completed: "sales_page.status_completed",
        paid: "sales_page.status_paid",
        partial: "sales_page.status_partial",
        due: "sales_page.status_due",
        unpaid: "sales_page.status_unpaid",
      };

      return statusKeyMap[value] ? t(statusKeyMap[value]) : value.charAt(0).toUpperCase() + value.slice(1);
    };

    const formatCurrency = (value?: number) => stateStore.formatPrice(Number(value || 0));

    const statusClass = (status?: string) => `status-${String(status || "pending").toLowerCase()}`;
    const paymentStatusClass = (status?: string) => `payment-${String(status || "unpaid").toLowerCase()}`;

    watch(
      () => props.sale?.id,
      () => {
        resetPaymentForm();
      }
    );

    loadPaymentMethods();

    return {
      paymentMethods,
      showPaymentForm,
      editingPaymentId,
      isSubmittingPayment,
      paymentForm,
      balanceDue,
      sortedPayments,
      resetPaymentForm,
      startCreatePayment,
      startEditPayment,
      submitPayment,
      removePayment,
      printReceipt,
      formatDate,
      formatStatus,
      formatCurrency,
      statusClass,
      paymentStatusClass,
      stateStore,
      t,
    };
  },
});
</script>

<style scoped>
.sales-details-dialog {
  max-width: 1180px;
}

.sales-status-badge,
.payment-status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 96px;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  text-transform: capitalize;
}

.status-completed,
.payment-paid {
  color: #1f9254;
  background: #e8f7ee;
}

.status-pending,
.payment-unpaid,
.payment-due {
  color: #d33c43;
  background: #fdecec;
}

.status-sent {
  color: #3563e9;
  background: #edf4ff;
}

.payment-partial {
  color: #b26a00;
  background: #fff4d6;
}

.details-panel,
.summary-card {
  border: 1px solid #edf0f7;
  border-radius: 18px;
  background: #fff;
}

.summary-card {
  padding: 20px 22px;
  min-height: 118px;
}

.summary-label {
  display: block;
  color: #697586;
  font-size: 14px;
  margin-bottom: 10px;
}

.details-panel {
  padding: 24px;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 20px;
}

.payment-list {
  display: grid;
  gap: 12px;
}

.payment-item {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 16px;
  border: 1px solid #edf0f7;
  border-radius: 14px;
  background: #fafbff;
}

.payment-empty-state {
  text-align: center;
  padding: 32px 20px;
  border: 1px dashed #d9deea;
  border-radius: 16px;
  background: #fbfcff;
}

.payment-empty-icon {
  width: 54px;
  height: 54px;
  margin: 0 auto 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #edf4ff;
  color: #5d5fef;
  font-size: 24px;
}

.payment-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

.totals-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #edf0f7;
}

.totals-row.grand {
  border-bottom: 0;
  padding-top: 14px;
  font-size: 17px;
}

:deep(.receipt-container) {
  max-width: 100%;
  padding: 0;
  box-shadow: none;
}

:deep(.receipt-table) {
  margin-bottom: 0;
}
</style>
